import { fromJS, List } from 'immutable'
import debug from 'debug'
import { reportType } from '@/enums/reportType'
import { processDesignerReport } from '@/store/utils/reportUtils'
import { reportHeaderType } from '@/enums/reportHeaderType'
import { getTreeCode } from '@/components/report-tables/core/utils'
import forOwn from 'lodash/forOwn'
import { getTreeKey } from '@/utils/getTreeCoordinates'

const log = debug('app:report-designer:reducer')

let autoSaved = window.localStorage.getItem('report-designer-autosaved')
if (autoSaved) autoSaved = { ...JSON.parse(autoSaved), column: null, row: null }

const maxHistorySize = 30

const clearStateObj = {
  uuid: null,
  history: [],
  activeIndex: 0,
  version: 1,
  autoSavedVersion: 0,
  savedVersion: 1,
  error: null,
  row: null,
  column: null,
  disabledActions: '',
  locale: '',
  view: 'names'
}

const clearState = setDisabledActions(fromJS(clearStateObj))
const initialState = setDisabledActions(fromJS(autoSaved || clearStateObj))

const actionTypes = {
  openTemplate: 'reportDesigner/openTemplate',
  autoSaved: 'reportDesigner/autoSaved',
  saved: 'reportDesigner/saved',
  select: 'reportDesigner/select',
  change: 'reportDesigner/change',
  error: 'reportDesigner/error',
  formulaAnalysis: 'reportDesigner/formulaAnalysis'
}

// let delayedLocalSaveHandle = 0

const actionCreators = {
  newTemplate: () => ({ type: actionTypes.openTemplate, template: prepareTemplate(newTemplate) }),
  openTemplate: (template) => ({ type: actionTypes.openTemplate, template: prepareTemplate(template) }),
  select: ({ colTreeCode, rowTreeCode }) => {
    return { type: actionTypes.select, column: colTreeCode, row: rowTreeCode }
  },
  autoSaved: (version) => ({ type: actionTypes.autoSaved, version }),
  saved: (version) => ({ type: actionTypes.saved, version }),
  change: (change, data) => ({ type: actionTypes.change, change, data }),
  error: (error) => ({ type: actionTypes.error, error }),
  formulaAnalysis: (data) => ({ type: actionTypes.formulaAnalysis, data: data })
}

function reducer (state = initialState, action) {
  switch (action.type) {
    case actionTypes.openTemplate: {
      return setDisabledActions(clearState.set('history', fromJS([action.template])).set('uuid', action.template.id || 't' + new Date().getTime()))
    }
    case actionTypes.autoSaved: {
      return state.set('autoSavedVersion', action.version)
    }
    case actionTypes.saved: {
      return state.set('savedVersion', action.version)
    }
    case actionTypes.select: {
      return setDisabledActions(state
        .set('column', action.column)
        .set('row', action.row))
    }
    case actionTypes.change: {
      const activeIndex = state.get('activeIndex')
      const template = state.getIn(['history', activeIndex])
      const column = state.get('column')
      const row = state.get('row')
      const columnCell = column ? template.get('allCols').valueSeq().find(x => x.get('treeCode') === column) : null
      const rowCell = row ? template.get('allRows').valueSeq().find(x => x.get('treeCode') === row) : null
      let parentTreeCode = null
      let order = 1
      if (columnCell && !rowCell) {
        parentTreeCode = template.getIn(['allCols', `${columnCell.get('parent')}`, 'treeCode'])
        order = columnCell.get('order')
      }
      if (!columnCell && rowCell) {
        parentTreeCode = template.getIn(['allRows', `${rowCell.get('parent')}`, 'treeCode'])
        order = rowCell.get('order')
      }

      switch (action.change) {
        case 'arabic': return state.set('locale', 'ar')
        case 'english': return state.set('locale', 'en')
        case 'names': return state.set('view', 'names')
        case 'codes': return state.set('view', 'codes')
        case 'undo': {
          return setDisabledActions(state.set('activeIndex', state.get('activeIndex') + 1))
        }
        case 'redo': {
          return setDisabledActions(state.set('activeIndex', state.get('activeIndex') - 1))
        }
        case 'meta':
        case 'edit': {
          // check whether selection location still exists
          if (!action.data.get('allCols').find(x => x.get('treeCode') === state.get('column')) ||
            !action.data.get('allRows').find(x => x.get('treeCode') === state.get('row'))) {
            state = state.set('column', null).set('row', null)
          }
          // TODO: check whether rowTitle is changing and propagate it to axisY.name / latinName
          return addNewVersion(state, action.data)
        }
        case 'moveLeft': {
          return (column && !row)
            ? moveHeader(state, template, column, 'allCols', -1)
            : state
        }
        case 'moveRight': {
          return (column && !row)
            ? moveHeader(state, template, column, 'allCols', 1)
            : state
        }
        case 'moveUp': {
          return (!column && row)
            ? moveHeader(state, template, row, 'allRows', -1)
            : state
        }
        case 'moveDown': {
          return (!column && row)
            ? moveHeader(state, template, row, 'allRows', 1)
            : state
        }
        case 'addLeft': {
          return (column && !row)
            ? addHeader(state, template, parentTreeCode, 'allCols', fromJS(newColumn).set('order', order))
            : state
        }
        case 'addRight': {
          return (column && !row)
            ? addHeader(state, template, parentTreeCode, 'allCols', fromJS(newColumn).set('order', order + 1))
            : state
        }
        case 'addUp': {
          return (!column && row)
            ? addHeader(state, template, parentTreeCode, 'allRows', fromJS(newRow).set('order', order))
            : state
        }
        case 'addDown': {
          return (!column && row)
            ? addHeader(state, template, parentTreeCode, 'allRows', fromJS(newRow).set('order', order + 1))
            : state
        }
        case 'addChild': {
          if (column && !row) {
            return addHeader(state, template, column, 'allCols', fromJS(newColumn))
          } else if (!column && row) {
            return addHeader(state, template, row, 'allRows', fromJS(newRow))
          }
          return state
        }
        case 'delete': {
          return deleteCell(state, template, column, row)
        }
        case 'changeParent': {
          const { cellType, oldTreeCode, newTreeCode, parentTreeCode, formulas } = action.data
          return changeParent(state, template, cellType === 'column' ? 'allCols' : 'allRows', oldTreeCode, newTreeCode, parentTreeCode, formulas)
        }
      }
      break
    }
    case actionTypes.error: {
      return state.set('error', action.error)
    }
    case actionTypes.formulaAnalysis: {
      const analysis = action.data
      if (analysis) {
        const templateCode = state.getIn(['history', state.get('activeIndex'), 'code'])

        const getDependencies = (dict, getKey) => {
          const deps = {}
          if (dict) {
            forOwn(dict, (v, k) => {
              if (v && v.dependencies && v.dependencies[templateCode]) {
                deps[getKey(k)] = v.dependencies[templateCode]
              }
            })
          }
          return deps
        }

        const dependencies = {
          ...getDependencies(analysis.cellFormulas, k => k),
          ...getDependencies(analysis.columnFormulas, k => getTreeKey(k, null)),
          ...getDependencies(analysis.rowFormulas, k => getTreeKey(null, k))
        }
        return state.set('dependencies', dependencies)
      }
      break
    }
  }
  return state
}

function moveHeader (state, template, treeCode, entities, movement) {
  const cell = template.get(entities).find(x => x.get('treeCode') === treeCode)
  const cellId1 = cell.get('id')
  const siblings = template.getIn([entities, `${cell.get('parent')}`, 'children']) // (cell.get('parent') ?  : template.get('children'))
    .sortBy(x => template.getIn([entities, `${x}`, 'order']))
  const index = siblings.indexOf(cellId1)
  if (index + movement >= 0 && index + movement < siblings.size) {
    const cellId2 = siblings.get(index + movement)
    let newVersion = template
      .updateIn([entities, `${cellId1}`, `order`], () => index + 1 + movement)
      .updateIn([entities, `${cellId2}`, `order`], () => index + 1)
    return addNewVersion(state, newVersion)
  }
  return state
}

function changeParent (state, template, entities, oldTreeCode, newTreeCode, parentTreeCode, formulas) {
  let newVersion = template

  function replaceTreeCode (treeCode) {
    if (typeof treeCode === 'string') {
      if (treeCode === oldTreeCode) {
        return newTreeCode
      }
      if (treeCode.startsWith(oldTreeCode + '.')) {
        return getTreeCode(newTreeCode, treeCode.substr(oldTreeCode.length + 1))
      }
    }
    return treeCode
  }

  function updateHeaderCell (headerCell) {
    const prevTreeCode = headerCell.get('treeCode')
    const oldParent = headerCell.get('parent')
    const isTargetCell = prevTreeCode === oldTreeCode
    const newParent = isTargetCell ? parentTreeCode : replaceTreeCode(oldParent)
    const nextTreeCode = isTargetCell ? newTreeCode : replaceTreeCode(prevTreeCode)

    headerCell = headerCell
      .set('parent', newParent || -1)
      .set('parentId', newParent || -1)
      .set('id', nextTreeCode)
      .set('treeCode', nextTreeCode)
      .update('children', List(), (x) => x.map(y => replaceTreeCode(y)))

    newVersion = newVersion
      .deleteIn([entities, prevTreeCode])
      .setIn([entities, nextTreeCode], headerCell)

    return headerCell
  }

  // update formulas
  if (formulas) {
    formulas.forEach(formula => {
      if (formula.colTreeCode && formula.rowTreeCode) { // body
        newVersion = newVersion.update('cells', x => x.map(y => y.get('colTreeCode') === formula.colTreeCode && y.get('rowTreeCode') === formula.rowTreeCode
          ? y.set('formula', formula.newFormula)
          : y
        ))
      } else if (formula.colTreeCode) { // col
        newVersion = newVersion.setIn(['allCols', `${formula.colTreeCode}`, 'formula'], formula.newFormula)
      } else if (formula.rowTreeCode) { // row
        newVersion = newVersion.setIn(['allRows', `${formula.rowTreeCode}`, 'formula'], formula.newFormula)
      }
    })
  }

  // moving sub-tree
  newVersion.get(entities).valueSeq().filter(x => x.get('treeCode').startsWith(`${oldTreeCode}.`))
    .forEach(x => updateHeaderCell(x))

  // change moving cell
  let headerCell = newVersion.getIn([entities, oldTreeCode])
  const oldParentTreeCode = headerCell.get('parentId')
  headerCell = updateHeaderCell(headerCell)

  // change original parent's data
  let childrenPath = [entities, `${oldParentTreeCode}`, 'children']
  newVersion = newVersion.updateIn(childrenPath, x => x.filter(y => y !== oldTreeCode))
  newVersion.getIn(childrenPath).sortBy(x => newVersion.getIn([entities, `${x}`, 'order'])).forEach((x, i) => {
    newVersion = newVersion.setIn([entities, `${x}`, 'order'], i + 1)
  })
  // change new parent's data
  childrenPath = [entities, `${parentTreeCode}`, 'children']
  newVersion = newVersion.updateIn(childrenPath, x => x.push(headerCell.get('treeCode')))
  newVersion.getIn(childrenPath).sortBy(x => newVersion.getIn([entities, `${x}`, 'order'])).forEach((x, i) => {
    newVersion = newVersion.setIn([entities, `${x}`, 'order'], i + 1)
  })

  return addNewVersion(state, newVersion)
}

function addHeader (state, template, parentTreeCode, entities, cell) {
  const parent = template.get(entities).find(x => x.get('treeCode') === parentTreeCode)
  // const newId = template.get(entities).keySeq().filter(x => isNumber(x)).map(x => parseInt(x, 10)).sort((a, b) => a - b).get(0) - 1
  const futureSiblings = template.get(entities).filter(x => parent.get('children').indexOf(x.get('treeCode')) >= 0)
  const existingCodes = futureSiblings.map(x => x.get('code')).valueSeq()
  // const order = futureSiblings.map(x => x.get('order')).valueSeq().max() + 1
  const newCode = getNewCode(existingCodes)
  const treeCode = parentTreeCode ? `${parentTreeCode}.${newCode}` : newCode
  cell = cell.set('id', treeCode)
    .set('parent', parent.get('id'))
    .set('parentId', parent.get('id'))
    .set('code', newCode)
    .set('treeCode', treeCode)
    .update('name', name => name + newCode).update('latinName', name => name + newCode)
    // .set('order', order)
  let newVersion = template
  newVersion = newVersion.setIn([entities, treeCode], cell)
  // update children array of the parent
  newVersion = newVersion.updateIn([entities, `${parent.get('id')}`, 'children'], x => x.push(treeCode))
  // for columns, ensure hasData is switched to false for parent cell when adding first child
  if (entities === 'allCols' && existingCodes.size === 0) {
    newVersion = newVersion.setIn([entities, `${parent.get('id')}`, 'hasData'], false)
  }
  // update order of all cells right from the inserted one
  parent.get('children').forEach(id => {
    newVersion = newVersion.updateIn([entities, `${id}`, 'order'], x => x >= cell.get('order') ? x + 1 : x)
  })
  return addNewVersion(state, newVersion)
}

function deleteCell (state, template, column, row) {
  let newVersion = template
  const columnCell = column ? template.get('allCols').find(x => x.get('treeCode') === column) : null
  const rowCell = row ? template.get('allRows').find(x => x.get('treeCode') === row) : null
  if (columnCell && !rowCell) {
    newVersion = deleteHeaderCell(template, 'allCols', columnCell)
  } else if (!columnCell && rowCell) {
    newVersion = deleteHeaderCell(template, 'allRows', rowCell)
  }
  newVersion = deleteBodyCells(newVersion, columnCell, rowCell)
  return newVersion !== template
    ? addNewVersion(state.set('column', null).set('row', null), newVersion)
    : state
}

function deleteBodyCells (newVersion, column, row) {
  if (column || row) {
    let filter
    if (column && !row) {
      filter = x => x.get('colTreeCode') !== column.get('treeCode')
    } else if (!column && row) {
      filter = x => x.get('rowTreeCode') !== row.get('treeCode')
    } else {
      filter = x => x.get('colTreeCode') !== column.get('treeCode') && x.get('rowTreeCode') !== row.get('treeCode')
    }
    newVersion = newVersion.set('cells', newVersion.get('cells').filter(filter))
  }
  return newVersion
}

function deleteHeaderCell (template, collection, cell) {
  if (cell && cell.get('children').size === 0) {
    const cellId = cell.get('id')
    template = template
      .deleteIn([collection, `${cellId}`])
      .updateIn([collection, `${cell.get('parent')}`, 'children'], x => x.delete(x.indexOf(cellId)))
    // correct order
    template.getIn([collection, `${cell.get('parent')}`, 'children']).sortBy(x => template.getIn([collection, `${x}`, 'order'])).forEach((x, i) => {
      template = template.setIn([collection, `${x}`, 'order'], i + 1)
    })
    // for columns, ensure hasData is switched to true for parent cell when removing last child
    if (collection === 'allCols') {
      const parent = template.getIn([collection, `${cell.get('parent')}`])
      if (parent && parent.get('children').size === 0) {
        template = template.setIn([collection, `${parent.get('id')}`, 'hasData'], true)
      }
    }
  }
  return template
}

function prepareTemplate (template) {
  checkTreeRecursions('columns', template.allCols, template.allCols['-1'], [])
  checkTreeRecursions('rows', template.allRows, template.allRows['-1'], [])
  return template
}

function getNewCode (existingCodes) {
  let counter = 1
  let code = '0001'
  while (existingCodes.indexOf(code) >= 0) {
    code = `000${++counter}`.slice(-4)
  }
  return code
}

function checkTreeRecursions (treeName, all, node, visited) {
  if (node.children) {
    const toRemove = []
    node.children.forEach(id => {
      if (visited.indexOf(id) >= 0) {
        // const circle = [...visited, id].map(x => all[x].code)
        log(`Template container circular dependencies in ${treeName} header tree, ids: ${[...visited, id].join('->')}`)
        toRemove.push(id)
      } else {
        checkTreeRecursions(treeName, all, all[id], [...visited, id])
      }
    })
    toRemove.forEach(id => {
      const index = node.children.indexOf(id)
      if (index >= 0) {
        node.children.splice(index, 1)
      }
    })
  }
}

function addNewVersion (state, newVersion) {
  let activeIndex = state.get('activeIndex')
  if (activeIndex > 0) {
    for (let i = 0; i < activeIndex; i++) {
      state = state.deleteIn(['history', 0])
    }
  }
  while (state.get('history').size >= maxHistorySize) {
    state = state.deleteIn(['history', state.get('history').size - 1])
  }
  return setDisabledActions(state
    .update('history', x => x.unshift(newVersion))
    .update('activeIndex', () => 0)
    .update('version', v => v + 1))
}

function setDisabledActions (state) {
  return state.set('disabledActions', getDisabledActions(state))
}

function getDisabledActions (state) {
  const activeIndex = state.get('activeIndex')
  const historySize = state.get('history').size
  const row = state.get('row')
  const column = state.get('column')
  const axisX = column && !row
  const axisY = !column && row
  const inBody = column && row
  let mostLeft = false
  let mostRight = false
  let mostTop = false
  let mostDown = false
  let canDelete = false
  let canChangeParent = false
  const template = state.getIn(['history', `${activeIndex}`])
  if (template) {
    if (axisX || axisY) {
      let cell = null
      let siblings = null
      if (axisX) {
        cell = template.get('allCols').find(x => x.get('treeCode') === column)
        siblings = getSiblings(template.get('allCols'), cell)
      } else if (axisY) {
        cell = template.get('allRows').find(x => x.get('treeCode') === row)
        siblings = getSiblings(template.get('allRows'), cell)
      }
      if (cell && siblings) {
        const cellCode = cell.get('code')
        const index = siblings.indexOf(cell)
        const lowest = index < 1
        const highest = index < 0 || siblings.size - index <= 1
        if (axisX) {
          mostLeft = lowest
          mostRight = highest
        } else if (axisY) {
          mostTop = lowest
          mostDown = highest
        }
        canDelete = cell.get('children').size === 0

        if ((axisX && template.get('allCols').size === 2) || (axisY && template.get('allRows').size === 2)) {
          canDelete = false
        }

        canChangeParent = !cell.get('siblingCode') && !siblings.find(x => x.get('siblingCode') === cellCode)
      }
    } else if (inBody) {
      const cell = template.get('cells')
        .find(x => x.get('colTreeCode') === column && x.get('rowTreeCode') === row)
      canDelete = !!cell
    }
  }

  const disabledActions = ([
    !(historySize > 1 && activeIndex < historySize - 1) && 'undo',
    !(historySize > 1 && activeIndex > 0) && 'redo',
    !(axisX && !mostLeft) && 'moveLeft',
    !(axisX && !mostRight) && 'moveRight',
    !(axisY && !mostTop) && 'moveUp',
    !(axisY && !mostDown) && 'moveDown',
    !(canChangeParent) && 'changeParent',

    !(axisX) && 'addLeft',
    !(axisX) && 'addRight',
    !(axisY) && 'addUp',
    !(axisY) && 'addDown',
    !(axisX || axisY) && 'addChild',
    !(canDelete) && 'delete',
    !(column || row) && 'copyCellLink.copyExplicitCellLink'
  ].filter(Boolean))

  return `.${disabledActions.join('.')}.`
}

function getSiblings (colsOrRows, cell) {
  if (cell) {
    const parentId = cell.get('parent')
    const ids = colsOrRows.getIn([`${parentId}`, 'children'])
    return ids.map(id => colsOrRows.get(`${id}`)).sortBy(x => x.get('order'))
  }
  return List()
}

const newColumn = {
  id: '0001',
  name: 'Column ',
  latinName: 'Column ',
  code: '0001',
  treeCode: '0001',
  order: 1,
  hasData: true,
  aggregationAllowed: true,
  children: []
}

const newRow = {
  id: '0001',
  reportHeaderType: reportHeaderType.enum.ClassicHeader,
  type: reportHeaderType.enum.ClassicHeader,
  name: 'Row ',
  latinName: 'Row ',
  code: '0001',
  treeCode: '0001',
  order: 1,
  aggregationAllowed: true,
  children: []
}

const newTemplate = processDesignerReport({
  name: 'New template name AR',
  latinName: 'New template name EN',
  title: 'New template title AR',
  latinTitle: 'New template title EN',
  rowTitle: 'Rows AR',
  latinRowTitle: 'Rows EN',
  code: 'untitled',
  colAxisCode: 'cac1',
  rowAxisCode: 'rac1',
  reportType: reportType.enum.Input,
  order: 1,
  cellMetadata: [],
  cells: [],
  cols: [newColumn],
  rows: [newRow]
})

export {
  reducer,
  initialState,
  actionCreators
}



// WEBPACK FOOTER //
// ./src/components/reportDesigner/reducer.js